import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { Avatar } from './Ui';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { useCart } from '../context/CartContext';
import { APP } from '../constants';
import { colors } from '../theme';
import { courierOnly, hasShopSide, isRoad } from '../utils/partnerTypes';
import { kindsOf, sidesOf } from '../utils/vertical';

/**
 * The signed-in chrome.
 *
 * Same five destinations as the app's tab bar, laid out the way each screen
 * size expects: a persistent sidebar plus a search-first top bar on desktop,
 * the app's bottom tab bar below 900px. Nothing here changes the workflow -
 * only where the same buttons sit.
 */

const USER_NAV = [
  { to: '/home', icon: '🏠', label: 'Home' },
  { to: '/services', icon: '🧭', label: 'Services' },
  { to: '/activity', icon: '📋', label: 'Activity' },
  { to: '/cart', icon: '🛒', label: 'Cart', badgeKey: 'cart' },
  { to: '/account', icon: '👤', label: 'Account' },
];

const USER_EXTRA = [
  { to: '/ride', icon: '🚕', label: 'Book a ride' },
  { to: '/jobs', icon: '💼', label: 'Jobs' },
  { to: '/vehicles', icon: '🚗', label: 'Vehicles' },
  { to: '/desire', icon: '✨', label: 'Your Desire' },
  { to: '/sell', icon: '🏪', label: 'Sell on KAAMWALA' },
];

// The nav follows everything the partner does: a driver who also sells gets both sides.
const partnerNav = (partner) => {
  const onRoad = isRoad(partner);
  const shopSide = hasShopSide(partner) || !onRoad;
  // The nav uses the trade's words: a hotel has Rooms and Stays, a dhaba a
  // Menu - and only a business that sells things gets Orders.
  const sides = sidesOf(partner);
  const kinds = kindsOf(partner);
  const hotelOnly = kinds.length === 1 && kinds[0] === 'hotel';
  const menuOnly = sides.menu && !sides.services;
  return [
    { to: '/partner', icon: '📊', label: 'Dashboard', end: true },
    ...(onRoad ? [{
      to: '/partner/rides',
      icon: courierOnly(partner) ? '📦' : '🚕',
      label: courierOnly(partner) ? 'Deliveries' : 'Rides',
    }] : []),
    ...(shopSide && sides.products ? [{ to: '/partner/orders', icon: '🧾', label: 'Orders' }] : []),
    ...(hotelOnly
      ? [{ to: '/partner/hotel?tab=bookings', icon: '🛎️', label: 'Stays' }, { to: '/partner/hotel?tab=rooms', icon: '🛏️', label: 'Rooms' }]
      : [{ to: '/partner/bookings', icon: '📅', label: 'Bookings' }]),
    ...(shopSide && !hotelOnly && (sides.products || sides.services)
      ? [{ to: '/partner/catalogue', icon: menuOnly ? '🍽️' : '📦', label: menuOnly ? 'Menu' : 'Catalogue' }] : []),
    { to: '/partner/business', icon: '🏪', label: 'Business' },
  ];
};

const isActive = (pathname, item, search = '') => {
  // A link to a tab (/partner/hotel?tab=rooms) is only lit on that tab.
  if (item.to.includes('?')) return `${pathname}${search}` === item.to;
  return item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);
};

const AppShell = ({ children, side = 'user' }) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { user, partner, isPartner, hasBusiness, setMode } = useAuth();
  const { location: place, unread, refreshUnread } = useApp();
  const { itemCount } = useCart();
  const [query, setQuery] = useState('');

  useEffect(() => { refreshUnread(); }, [refreshUnread, pathname]);

  const nav = side === 'partner' ? partnerNav(partner) : USER_NAV;
  const badges = { cart: itemCount };

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(query.trim() ? `/search?q=${encodeURIComponent(query.trim())}` : '/search');
  };

  return (
    <div className="shell">
      <header className="topbar">
        <Link to={side === 'partner' ? '/partner' : '/home'} className="row" style={{ gap: 10 }}>
          <Logo size={34} />
          <span
            style={{
              fontWeight: 900, letterSpacing: 0.6, color: colors.primary, fontSize: 17,
            }}
            className="hide-sm"
          >
            {APP.name}
          </span>
        </Link>

        {side === 'user' ? (
          <>
            <button
              type="button"
              onClick={() => navigate('/location')}
              className="row press hide-sm"
              style={{
                background: colors.bg, border: `1px solid ${colors.line}`,
                borderRadius: 12, padding: '7px 12px', gap: 7, maxWidth: 210,
              }}
            >
              <span>📍</span>
              <span style={{ textAlign: 'left', minWidth: 0 }}>
                <span className="t-tiny" style={{ display: 'block', lineHeight: 1 }}>DELIVERING TO</span>
                <span className="t-title clamp-1" style={{ fontSize: 13 }}>
                  {place.label || place.city}
                </span>
                {place.address ? (
                  <span className="t-tiny clamp-1" style={{ display: 'block', lineHeight: 1.2 }}>{place.address}</span>
                ) : null}
              </span>
            </button>

            <form onSubmit={submitSearch} className="grow" style={{ maxWidth: 460 }}>
              <div className="input-wrap" style={{ background: colors.bg }}>
                <span>🔍</span>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search services, shops, products, jobs…"
                  style={{ padding: '10px 0', fontSize: 14 }}
                  aria-label="Search KAAMWALA"
                />
              </div>
            </form>
          </>
        ) : (
          <div className="grow">
            <div className="t-tiny">PARTNER DASHBOARD</div>
            <div className="t-title clamp-1">{(partner && partner.businessName) || (user && user.name)}</div>
          </div>
        )}

        <Link to="/notifications" className="press topbar-icon" aria-label="Notifications">
          🔔
          {unread > 0 ? (
            <span
              style={{
                position: 'absolute', top: 2, right: 2, background: colors.accent, color: '#fff',
                minWidth: 17, height: 17, borderRadius: 9, fontSize: 9.5, fontWeight: 800,
                display: 'grid', placeItems: 'center', padding: '0 4px',
              }}
            >
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </Link>

        <Link
          to={side === 'partner' ? '/partner/business' : '/account'}
          className="press topbar-icon"
          aria-label="My account"
        >
          <Avatar name={user && user.name} uri={user && user.avatar} size={34} />
        </Link>
      </header>

      <div className="shell-body">
        <nav className="sidebar">
          {nav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`side-link ${isActive(pathname, item, search) ? 'active' : ''}`}
            >
              <span className="ic" aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
              {item.badgeKey && badges[item.badgeKey] > 0 ? (
                <span className="side-badge">{badges[item.badgeKey]}</span>
              ) : null}
            </Link>
          ))}

          {side === 'user' ? (
            <>
              <div className="t-tiny" style={{ margin: '20px 13px 8px', letterSpacing: 1 }}>MORE ON KAAMWALA</div>
              {USER_EXTRA.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`side-link ${isActive(pathname, item, search) ? 'active' : ''}`}
                >
                  <span className="ic" aria-hidden="true">{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              ))}
              {isPartner && hasBusiness ? (
                <button
                  type="button"
                  className="side-link"
                  style={{ width: '100%', background: 'none', border: 0, textAlign: 'left' }}
                  onClick={() => { setMode('partner'); navigate('/partner'); }}
                >
                  <span className="ic">🏪</span>
                  <span>Switch to partner panel</span>
                </button>
              ) : null}
            </>
          ) : (
            <>
              <div className="t-tiny" style={{ margin: '20px 13px 8px', letterSpacing: 1 }}>
                {isRoad(partner) ? 'WORK' : 'HIRING'}
              </div>
              {isRoad(partner) ? (
                <>
                  <Link to="/jobs" className={`side-link ${pathname.startsWith('/jobs') ? 'active' : ''}`}>
                    <span className="ic">💼</span>
                    <span>Find a job</span>
                  </Link>
                  <Link to="/my-applications" className={`side-link ${pathname.startsWith('/my-applications') ? 'active' : ''}`}>
                    <span className="ic">📄</span>
                    <span>My applications</span>
                  </Link>
                </>
              ) : (
                <Link to="/partner/jobs" className={`side-link ${pathname.startsWith('/partner/jobs') ? 'active' : ''}`}>
                  <span className="ic">💼</span>
                  <span>Jobs &amp; applicants</span>
                </Link>
              )}
              {isPartner && hasBusiness ? (
                <button
                  type="button"
                  className="side-link"
                  style={{ width: '100%', background: 'none', border: 0, textAlign: 'left' }}
                  onClick={() => { setMode('user'); navigate('/home'); }}
                >
                  <span className="ic">🙋</span>
                  <span>Switch to customer</span>
                </button>
              ) : null}
            </>
          )}

          <div className="t-tiny" style={{ margin: '24px 13px 0', lineHeight: 1.6 }}>
            {APP.name} · {APP.tagline}
            <br />
            Support {APP.supportPhone}
          </div>
        </nav>

        <main className="page">{children}</main>
      </div>

      <nav className="tabbar">
        {nav.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`tab ${isActive(pathname, item, search) ? 'active' : ''}`}
          >
            <span className="ic" aria-hidden="true">{item.icon}</span>
            <span>{item.label}</span>
            {item.badgeKey && badges[item.badgeKey] > 0 ? (
              <span className="tab-badge">{badges[item.badgeKey] > 99 ? '99+' : badges[item.badgeKey]}</span>
            ) : null}
          </Link>
        ))}
      </nav>
    </div>
  );
};

export default AppShell;
